import type { ReconciliationComments } from '@billing/validation';
import { CLAIM_TARIFF_STATUS } from '@billing/validation/tariff';
import { MRT_Row } from 'mantine-react-table';
import { UseMutationResult } from '@tanstack/react-query';
import { Claim } from 'interfaces';
import {
  ClaimTableRow,
  getClaimTariffRowDebits,
  isClaimParentRow,
  isClaimTableRow,
  isClaimTariffRow,
} from '../utils/claimTableRows';

interface BulkReconcileTariffsParams extends ReconciliationComments {
  rows: MRT_Row<ClaimTableRow>[];
  bulkReconcileTariffsMutation: UseMutationResult<Claim[], Error>;
  recordAdjustment?: boolean;
}

export interface BulkReconcileTariffsResult {
  reconciledClaims: number;
  updatedDebits: number;
  claims?: Claim[];
}

interface ReconcileSelection {
  claim: Claim;
  selectedDebitIds: Set<string>;
  selectedTariffIds: Set<string>;
}

interface BulkReconcileTariffsPayload extends ReconciliationComments {
  claimID: string;
  debitIDs: string[];
  tariffIDs: string[];
}

const countUpdatedDebits = (
  debits: NonNullable<Claim['debits']>,
  selectedDebitIds: Set<string>,
  nextDebits: NonNullable<Claim['debits']>
) =>
  nextDebits.filter((debit, index) => {
    if (!selectedDebitIds.has(debit.id)) return false;
    return debit.requestedAmount !== debits[index]?.requestedAmount;
  }).length;

export const bulkReconcileTariffs = async ({
  rows,
  bulkReconcileTariffsMutation,
  recordAdjustment = false,
  comment,
  internalComment,
}: BulkReconcileTariffsParams): Promise<BulkReconcileTariffsResult> => {
  let reconciledClaims = 0;
  let updatedDebits = 0;
  const selectionsByClaimID = new Map<string, ReconcileSelection>();

  for (const row of rows as MRT_Row<ClaimTableRow | Claim>[]) {
    const rowData = row.original;
    const claim = isClaimTariffRow(rowData)
      ? rowData.parentClaim
      : (rowData as Claim);
    const claimID = claim.id;
    const existing = selectionsByClaimID.get(claimID);
    const selection: ReconcileSelection = existing ?? {
      claim,
      selectedDebitIds: new Set<string>(),
      selectedTariffIds: new Set<string>(),
    };

    if (isClaimParentRow(rowData) || !isClaimTableRow(rowData)) {
      for (const debit of claim.debits ?? []) {
        selection.selectedDebitIds.add(debit.id);
      }
      for (const tariff of claim.tariffs ?? []) {
        selection.selectedTariffIds.add(tariff.id);
      }
    } else if (isClaimTariffRow(rowData)) {
      selection.selectedTariffIds.add(rowData.tariffId);
      for (const debit of getClaimTariffRowDebits(rowData)) {
        selection.selectedDebitIds.add(debit.id);
      }
    }

    selectionsByClaimID.set(claimID, selection);
  }

  for (const {
    claim,
    selectedDebitIds,
    selectedTariffIds,
  } of selectionsByClaimID.values()) {
    const pendingTariffIDs = new Set(
      (claim.tariffs ?? [])
        .filter((tariff) => tariff.status === CLAIM_TARIFF_STATUS.PENDING)
        .map((tariff) => tariff.id)
    );
    for (const tariffID of pendingTariffIDs) selectedTariffIds.delete(tariffID);
    for (const debit of claim.debits ?? []) {
      if (pendingTariffIDs.has(debit.tariffID))
        selectedDebitIds.delete(debit.id);
    }
    if (!selectedTariffIds.size && !selectedDebitIds.size) {
      selectionsByClaimID.delete(claim.id);
      continue;
    }
    const debits = claim.debits ?? [];
    if (selectedDebitIds.size === 0) {
      if (selectedTariffIds.size > 0) {
        reconciledClaims += 1;
      }
      continue;
    }

    reconciledClaims += 1;

    const rejectionTariffIDs = new Set(
      (claim.tariffs ?? [])
        .filter(
          (tariff) =>
            tariff.status === CLAIM_TARIFF_STATUS.REJECTED ||
            tariff.status === CLAIM_TARIFF_STATUS.UNCLAIMABLE
        )
        .map((tariff) => tariff.id)
    );
    const nextDebits = recordAdjustment
      ? debits.map((debit) => {
          const isSelected = selectedDebitIds.has(debit.id);
          if (!isSelected || rejectionTariffIDs.has(debit.tariffID))
            return debit;

          return debit.paidAmount && debit.paidAmount !== debit.requestedAmount
            ? { ...debit, requestedAmount: debit.paidAmount }
            : debit;
        })
      : debits;

    updatedDebits += countUpdatedDebits(debits, selectedDebitIds, nextDebits);
  }

  if (selectionsByClaimID.size > 0) {
    const payload: BulkReconcileTariffsPayload[] = Array.from(
      selectionsByClaimID.values()
    ).map(({ claim, selectedDebitIds, selectedTariffIds }) => ({
      claimID: claim.id,
      debitIDs: Array.from(selectedDebitIds),
      tariffIDs: Array.from(selectedTariffIds),
      comment,
      internalComment,
    }));

    const claims = await bulkReconcileTariffsMutation.mutateAsync({
      values: payload,
      recordAdjustment,
    });

    return { reconciledClaims, updatedDebits, claims };
  }

  return { reconciledClaims, updatedDebits };
};
